import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  completeGoogleProfile,
  googleLawyerAuth,
  loginLawyer,
  persistLawyerSession,
  registerLawyer,
  sendOtp,
  verifyOtp,
  verifySession,
} from "../services/authService";
import { fetchDashboardProfile } from "../services/lawyerService";
import { sessionStorage } from "../services/http";

const AuthContext = createContext(null);

function getIsVerified(user) {
  if (!user) return false;
  if (typeof user.isVerified === "boolean") return user.isVerified;
  if (typeof user.is_verified === "boolean") return user.is_verified;
  return false;
}

function readStoredUser() {
  const raw = localStorage.getItem(sessionStorage.USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function mergeUserWithProfile(user, profile) {
  if (!user || !profile) return user;

  return {
    ...user,
    name: profile.full_name || profile.name || user.name,
    email: profile.email || user.email,
    phone: profile.phone || user.phone,
    location: profile.city || user.location || user.city || "",
    city: profile.city || user.city || user.location || "",
    state: profile.state || user.state || "",
    practiceAreas: Array.isArray(profile.practice_areas) ? profile.practice_areas : user.practiceAreas,
    specialization:
      (Array.isArray(profile.practice_areas) && profile.practice_areas[0]) ||
      user.specialization ||
      "",
    caseTypes: Array.isArray(profile.case_types) ? profile.case_types : user.caseTypes || user.case_types || [],
    languages: Array.isArray(profile.languages) ? profile.languages : user.languages || [],
    courts: Array.isArray(profile.courts) ? profile.courts : user.courts || [],
    experience: profile.experience ?? user.experience,
    barEnrollmentYear: profile.barEnrollmentYear || profile.bar_enrollment_year || user.barEnrollmentYear || "",
    consultation_fee: profile.consultation_fee ?? profile.consultationFee ?? user.consultation_fee ?? user.consultationFee,
    consultationFee: profile.consultationFee ?? profile.consultation_fee ?? user.consultationFee ?? user.consultation_fee,
    profile_photo_url: profile.profile_photo_url || profile.profilePhoto || user.profile_photo_url || user.profilePhoto,
    profilePhoto: profile.profilePhoto || profile.profile_photo_url || user.profilePhoto || user.profile_photo_url,
    barCouncilNo: profile.enrollment_number || user.barCouncilNo,
    age: profile.age ?? user.age,
    college: profile.college || user.college || "",
    panCard: profile.panCard || profile.pan_card || user.panCard || user.pan_card || "",
    workplace: profile.workplace || user.workplace || "",
    address: profile.address || user.address || "",
    workAddress: profile.workAddress || profile.work_address || user.workAddress || user.work_address || "",
    barCouncilIdDocument: profile.barCouncilIdDocument || profile.bar_council_id_document || user.barCouncilIdDocument || user.bar_council_id_document || "",
    lawyerProfileId: profile.id || profile.lawyerProfileId || profile.lawyer_profile_id || user.lawyerProfileId || "",
    // Always trust backend profileComplete — never recompute on frontend
    profileComplete: true,
    // isVerified is cosmetic only — never used to restrict navigation
    isVerified: getIsVerified(user),
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem(sessionStorage.TOKEN_KEY)));
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      // Cross-domain login: check URL for token
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        localStorage.setItem(sessionStorage.TOKEN_KEY, tokenFromUrl);
        // Remove token from URL for security
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const token = localStorage.getItem(sessionStorage.TOKEN_KEY);
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setIsBootstrapping(false);
        return;
      }

      try {
        const session = await verifySession();
        const storedUser = readStoredUser();
        const baseUser = {
          ...(storedUser || {}),
          ...(session?.user || {}),
          id: session?.legacy_user_id || session?.user?._id || session?.user?.id || storedUser?.id || "",
          isVerified:
            typeof session?.user?.isVerified === "boolean"
              ? session.user.isVerified
              : typeof session?.user?.is_verified === "boolean"
                ? session.user.is_verified
                : getIsVerified(storedUser),
          profileComplete:
            typeof session?.user?.profileComplete === "boolean"
              ? session.user.profileComplete
              : typeof storedUser?.profileComplete === "boolean"
                ? storedUser.profileComplete
                : false,
        };
        let mergedUser = baseUser;
        try {
          const profile = await fetchDashboardProfile();
          mergedUser = mergeUserWithProfile(baseUser, profile);
          if (mergedUser) {
            localStorage.setItem(sessionStorage.USER_KEY, JSON.stringify(mergedUser));
          }
        } catch {
          // Non-blocking: auth session should still succeed even if profile fetch fails.
        }

        setIsAuthenticated(true);
        setUser(mergedUser);
      } catch {
        sessionStorage.clearSession();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsBootstrapping(false);
      }
    };

    bootstrap();
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isBootstrapping,
      async login(payload) {
        const response = await loginLawyer(payload);
        if (!response?.token || !response?.user) {
          throw new Error("Invalid login response");
        }

        let nextUser = response.user;
        persistLawyerSession({ token: response.token, user: nextUser });
        try {
          const profile = await fetchDashboardProfile();
          nextUser = mergeUserWithProfile(nextUser, profile);
          persistLawyerSession({ token: response.token, user: nextUser });
        } catch {
          // Keep login successful even if profile fetch is unavailable.
        }

        setUser(nextUser);
        setIsAuthenticated(true);
        return { ...response, user: nextUser };
      },
      async register(payload) {
        return registerLawyer(payload);
      },
      async loginWithGoogle(idToken) {
        const response = await googleLawyerAuth(idToken, "lawyer");
        if (response?.isNewUser) {
          return response;
        }
        if (!response?.token || !response?.user) {
          throw new Error("Invalid Google login response");
        }

        let nextUser = response.user;
        persistLawyerSession({ token: response.token, user: nextUser });
        try {
          const profile = await fetchDashboardProfile();
          nextUser = mergeUserWithProfile(nextUser, profile);
          persistLawyerSession({ token: response.token, user: nextUser });
        } catch {
          // Keep login successful even if profile fetch is unavailable.
        }

        setUser(nextUser);
        setIsAuthenticated(true);
        return { ...response, user: nextUser };
      },
      async registerWithGoogle(idToken) {
        const response = await googleLawyerAuth(idToken, "lawyer");
        if (response?.isNewUser) {
          return response;
        }
        if (!response?.token || !response?.user) {
          throw new Error("Invalid Google signup response");
        }

        let nextUser = response.user;
        persistLawyerSession({ token: response.token, user: nextUser });
        try {
          const profile = await fetchDashboardProfile();
          nextUser = mergeUserWithProfile(nextUser, profile);
          persistLawyerSession({ token: response.token, user: nextUser });
        } catch {
          // Keep signup successful even if profile fetch is unavailable.
        }

        setUser(nextUser);
        setIsAuthenticated(true);
        return { ...response, user: nextUser };
      },
      async completeGoogleSignup(payload) {
        const response = await completeGoogleProfile(payload);
        if (!response?.token || !response?.user) {
          throw new Error("Invalid complete-profile response");
        }

        let nextUser = response.user;
        persistLawyerSession({ token: response.token, user: nextUser });
        try {
          const profile = await fetchDashboardProfile();
          nextUser = mergeUserWithProfile(nextUser, profile);
          persistLawyerSession({ token: response.token, user: nextUser });
        } catch {
          // Keep signup completion successful even if profile fetch is unavailable.
        }

        setUser(nextUser);
        setIsAuthenticated(true);
        return { ...response, user: nextUser };
      },
      async requestOtp(phone) {
        return sendOtp(phone);
      },
      async confirmOtp(phone, otp) {
        return verifyOtp(phone, otp);
      },
      logout() {
        sessionStorage.clearSession();
        setIsAuthenticated(false);
        setUser(null);
        window.location.href = "/login?role=lawyer";
      },
      updateProfile(updates) {
        setUser((prev) => {
          const next = { ...(prev || {}), ...(updates || {}) };
          // Always keep profileComplete=true if the caller passes it.
          // Never recompute it via frontend logic — trust the backend.
          if (String(next.role || "").toLowerCase() === "lawyer") {
            if (typeof (updates || {}).profileComplete !== "boolean") {
              next.profileComplete = true; // default to true; backend controls this
            }
          }
          localStorage.setItem(sessionStorage.USER_KEY, JSON.stringify(next));
          return next;
        });
      },
    }),
    [isAuthenticated, isBootstrapping, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
